"use client";

import {
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";

import { useEffect, useId, useRef, useState } from "react";

const dayLabels = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pa"];
const monthFormatter = new Intl.DateTimeFormat("tr-TR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function parse(value?: string) {
  return value ? new Date(`${value}T00:00:00.000Z`) : null;
}
function iso(value: Date) {
  return value.toISOString().slice(0, 10);
}
function addDays(value: Date, days: number) {
  return new Date(value.getTime() + days * 86_400_000);
}
function monthStart(value: Date) {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
}
function addMonths(value: Date, months: number) {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + months, 1));
}
function sameDay(left: Date | null, right: Date) {
  return Boolean(left && iso(left) === iso(right));
}

function Calendar({
  month,
  from,
  to,
  min,
  max,
  onSelect,
  onNavigate,
}: {
  month: Date;
  from: Date | null;
  to: Date | null;
  min: Date;
  max: Date;
  onSelect: (date: Date) => void;
  onNavigate: (month: Date) => void;
}) {
  const days = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const leading = (month.getUTCDay() + 6) % 7;
  const cells = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from(
      { length: days },
      (_, index) =>
        new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), index + 1)),
    ),
  ];
  function keyboard(event: React.KeyboardEvent<HTMLButtonElement>, date: Date) {
    const offset =
      event.key === "ArrowRight"
        ? 1
        : event.key === "ArrowLeft"
          ? -1
          : event.key === "ArrowDown"
            ? 7
            : event.key === "ArrowUp"
              ? -7
              : 0;
    if (!offset) return;
    event.preventDefault();
    let target = addDays(date, offset);
    if (target < min) target = min;
    if (target > max) target = max;
    if (target === date) return;
    if (target.getUTCMonth() !== month.getUTCMonth()) onNavigate(monthStart(target));
    window.setTimeout(
      () =>
        document
          .querySelector<HTMLButtonElement>(`[data-calendar-date="${iso(target)}"]`)
          ?.focus(),
      0,
    );
  }
  return (
    <section className="date-range-calendar" aria-label={monthFormatter.format(month)}>
      <h3>{monthFormatter.format(month)}</h3>
      <div className="date-range-weekdays" aria-hidden="true">
        {dayLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="date-range-days">
        {cells.map((date, index) =>
          date ? (
            <button
              type="button"
              key={iso(date)}
              data-calendar-date={iso(date)}
              aria-label={dateFormatter.format(date)}
              aria-pressed={sameDay(from, date) || sameDay(to, date)}
              disabled={date < min || date > max}
              className={[
                sameDay(min, date) ? "is-today" : "",
                sameDay(from, date) ? "is-start" : "",
                sameDay(to, date) ? "is-end" : "",
                from && to && date > from && date < to ? "is-range" : "",
                date < min || date > max ? "is-disabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelect(date)}
              onKeyDown={(event) => keyboard(event, date)}
            >
              {date.getUTCDate()}
            </button>
          ) : (
            <span key={`blank-${index}`} />
          ),
        )}
      </div>
    </section>
  );
}

export function DateRangePicker({
  from: initialFrom,
  to: initialTo,
  minIso,
  maxIso,
}: {
  from?: string;
  to?: string;
  /** Server-computed booking window (authoritative clock). */
  minIso: string;
  maxIso: string;
}) {
  const labelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const min = parse(minIso) ?? new Date(0);
  const max = parse(maxIso) ?? new Date(8_640_000_000_000);
  const clampDate = (value: Date | null) =>
    value ? (value < min ? min : value > max ? max : value) : null;
  const [committedFrom, setCommittedFrom] = useState<Date | null>(
    clampDate(parse(initialFrom)),
  );
  const [committedTo, setCommittedTo] = useState<Date | null>(
    clampDate(parse(initialTo)),
  );
  const [from, setFrom] = useState<Date | null>(committedFrom);
  const [to, setTo] = useState<Date | null>(committedTo);
  const initialMonth = monthStart(committedFrom ?? min);
  const [month, setMonth] = useState(() => {
    const minMonth = monthStart(min);
    const maxMonth = monthStart(max);
    return initialMonth < minMonth
      ? minMonth
      : initialMonth > maxMonth
        ? maxMonth
        : initialMonth;
  });
  const minMonth = monthStart(min);
  const maxMonth = monthStart(max);
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setFrom(committedFrom);
        setTo(committedTo);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    window.setTimeout(
      () =>
        panel.current
          ?.querySelector<HTMLButtonElement>(
            "[aria-pressed=true], [data-calendar-date]",
          )
          ?.focus(),
      0,
    );
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open, committedFrom, committedTo]);
  function select(date: Date) {
    if (date < min || date > max) return;
    if (!from || to || date < from) {
      setFrom(date);
      setTo(null);
    } else setTo(date);
  }
  function close() {
    setOpen(false);
    setFrom(committedFrom);
    setTo(committedTo);
    window.setTimeout(() => trigger.current?.focus(), 0);
  }
  function apply() {
    if (from && !to) setTo(from);
    setCommittedFrom(from);
    setCommittedTo(to ?? from);
    close();
  }
  function preset(days: number) {
    // Start from the server-provided "today" (min), never the client clock.
    const start = min;
    const end = addDays(start, days - 1);
    setFrom(start);
    setTo(end > max ? max : end);
    setMonth(monthStart(start));
  }
  const summary = committedFrom
    ? `${dateFormatter.format(committedFrom)}${committedTo ? ` – ${dateFormatter.format(committedTo)}` : ""}`
    : "Tarih aralığı seçin";
  return (
    <div
      ref={containerRef}
      className="date-range-picker public-search__item"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) close();
      }}
    >
      <span className="public-search__icon" aria-hidden="true">
        <CalendarBlankIcon size={24} weight="regular" />
      </span>
      <div className="public-search__field-body">
        <span id={labelId} className="public-search__field-label">
          Tarihleri seçin
        </span>
        <button
          ref={trigger}
          className="date-range-trigger"
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="date-range-trigger__text">{summary}</span>
        </button>
      </div>
      <input
        type="hidden"
        name="departureFrom"
        value={committedFrom ? iso(committedFrom) : ""}
      />
      <input
        type="hidden"
        name="departureTo"
        value={committedTo ? iso(committedTo) : ""}
      />
      {open ? (
        <div
          ref={panel}
          className="date-range-popover"
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelId}
        >
          <header>
            <button
              type="button"
              aria-label="Önceki ay"
              disabled={month <= minMonth}
              onClick={() =>
                setMonth((value) => {
                  const previous = addMonths(value, -1);
                  return previous < minMonth ? minMonth : previous;
                })
              }
            >
              <CaretLeftIcon size={16} />
            </button>
            <strong>Tarih aralığı</strong>
            <button
              type="button"
              aria-label="Sonraki ay"
              disabled={month >= maxMonth}
              onClick={() =>
                setMonth((value) => {
                  const next = addMonths(value, 1);
                  return next > maxMonth ? maxMonth : next;
                })
              }
            >
              <CaretRightIcon size={16} />
            </button>
          </header>
          <div className="date-range-presets">
            <button type="button" onClick={() => preset(30)}>
              Önümüzdeki 30 gün
            </button>
            <button type="button" onClick={() => preset(90)}>
              Önümüzdeki 3 ay
            </button>
            <button type="button" onClick={() => preset(180)}>
              Önümüzdeki 6 ay
            </button>
          </div>
          <div className="date-range-calendars">
            <Calendar
              month={month}
              from={from}
              to={to}
              min={min}
              max={max}
              onSelect={select}
              onNavigate={(target) =>
                setMonth(target < minMonth ? minMonth : target > maxMonth ? maxMonth : target)
              }
            />
            <Calendar
              month={addMonths(month, 1) > maxMonth ? maxMonth : addMonths(month, 1)}
              from={from}
              to={to}
              min={min}
              max={max}
              onSelect={select}
              onNavigate={(value) => {
                const target = addMonths(value, -1);
                setMonth(target < minMonth ? minMonth : target > maxMonth ? maxMonth : target);
              }}
            />
          </div>
          <p className="sr-only" aria-live="polite">
            {from ? `Başlangıç ${dateFormatter.format(from)}` : "Başlangıç seçilmedi"}.{" "}
            {to ? `Bitiş ${dateFormatter.format(to)}` : "Bitiş seçilmedi"}.
          </p>
          <footer>
            <button
              type="button"
              className="date-range-btn-clear"
              onClick={() => {
                setFrom(null);
                setTo(null);
              }}
            >
              Temizle
            </button>
            <button type="button" className="date-range-btn-apply" onClick={apply}>
              Uygula
            </button>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
